// React and Context
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// PrimeReact Components
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';

// Contexts and APIs
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { AssignableCreator, DeviceTable } from 'apps/marsinet/src/app/typescript-axios';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';

// Utilities
import { configureAxios } from 'apps/marsinet/src/app/utils/utils';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import { ProgressBarComponent } from 'apps/marsinet/src/app/components/progress-bar/progress-bar';
import ModalAssignDeviceToOrganization from './components/modal-assign-device-to-organization';
import { AvailableResourceActions, can, DEVICE_CATEGORIES, DeviceState, ROLES } from '@marsinet/commons';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';

import ModalSuggestDeviceState from 'apps/marsinet/src/app/components/modal-suggest-device-state/modal-suggest-device-state';

export function Institution() {
  const { id_institution } = useParams();
  const { access_token, setError, admin, role } = useContext(MarsinetContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast: show } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [institution, setInstitution] = useState<any | null>(null);
  const [deviceToSuggest, setDeviceToSuggest] = useState<DeviceTable | null>(null);
  const [contactData, setContactData] = useState(null);
  const [editingCreator, setEditingCreator] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(
    null
  );
  const [visible, setVisible] = useState<boolean>(false);
  const [updateChanges, setUpdateChanges] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);

  const [availableCreatorUsers, setAvailableCreatorUsers] = useState<AssignableCreator[]>();
  const [creatorUser, setCreatorUser] = useState<AssignableCreator>();

  const canViewInstitutionPage =
    role && can(role, AvailableResourceActions.organizations.view_page, admin);
  const canDeleteDeviceFromInstitution =
    role &&
    can(role, AvailableResourceActions.organizations.update_del_devices, admin);
  const canAssignDeviceToInstitution =
    role &&
    can(role, AvailableResourceActions.organizations.update_add_devices, admin);

  const canDeleteInstitution =
    role && can(role, AvailableResourceActions.organizations.delete);

  useEffect(() => {
    if (!access_token || !id_institution || !canViewInstitutionPage) return;

    configureAxios(access_token);

    const fetchInstitution = async () => {
      const { data } =
        await apiServices.adminApi.adminControllerFindOneOrganization(
          id_institution
        );
      return data;
    };

    const loadUsers = async (creator) => {
      try {
        const creatorUsers: any = await apiServices.userApi.usersControllerGetAvailableOrganizationCreators(id_institution);

        setAvailableCreatorUsers(creatorUsers.data);
        setCreatorUser(creatorUsers.data?.find((u) => u.u_id === creator));
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
            error_page: window.location.pathname,
            error_stack: error.stack || 'No stack trace',
            timestamp: new Date().toISOString(),
            browser_info: navigator.userAgent,
          });
        }
      }
    };

    const fetchContactData = async (o_id) => {
      if (!o_id) {
        return {
          name: t('noData'),
          phone: t('noData'),
          email: t('noData'),
        };
      }
      try {
        const { data } =
          await apiServices.userApi.usersControllerGetUsersOrganization(o_id);
        return data;
      } catch {
        return {
          name: t('noData'),
          phone: t('noData'),
          email: t('noData'),
        };
      }
    };
    const fetchDevices = async (o_id) => {
      try {
        const { data } =
          await apiServices.devicesApi.devicesControllerFindAllDevicesOfOrganization(
            o_id
          );
        return data;
      } catch (error) {
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
        });
        return [];
      }
    };

    const fetchData = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const institutionData = await fetchInstitution();
        setInstitution(institutionData);
        await loadUsers(institutionData.created_by);

        const contactInfo = await fetchContactData(id_institution);
        setContactData(contactInfo);

        const devices = await fetchDevices(id_institution);
        setInstitution((prev) => ({
          ...prev,
          devices: devices.length > 0 ? devices : [],
        }));
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [access_token, id_institution, updateChanges, role]);

  const navigateToInstitutions = () => {
    setTimeout(() => {
      navigate('/authenticated/institutions');
    }, 700);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(institution?.o_id ?? id_institution ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const confirmDeleteInstitution = (
    id: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    confirmDialog({
      message: t('pages.institutions.messages.deleteConfirm'),
      header: t('pages.institutions.messages.deleteConfirmTitle'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('yes'),
      rejectLabel: t('no'),
      acceptClassName: 'p-button-danger',
      accept: () => {
        handleDeleteInstitution(id);
      },
    });
  };

  const handleDeleteInstitution = async (id: string) => {
    try {
      await apiServices.adminApi.adminControllerDeleteOrganization(id);
      show('success', t('messages.success'), t('messages.org.delete_org'));
      navigateToInstitutions();
    } catch (_error) {
      show('error', t('messages.error'), t('messages.org.delete_org_error'));
    }
  };

  const canEditDeviceState =
    role && can(role, AvailableResourceActions.devices.update_state, admin);

  const shouldSuggestState = (device: DeviceTable) => {
    const isClientOrganization =
      institution?.role === ROLES.CLINIC || institution?.role === ROLES.PERSONAL;

    const alreadyOnClient = DEVICE_CATEGORIES.on_client.includes(
      device.device_state as DeviceState
    );

    return !!canEditDeviceState && isClientOrganization && !alreadyOnClient;
  };

  const assignDeviceToOrganizationFunction = async (device: DeviceTable) => {
    try {
      await apiServices.devicesApi.devicesControllerAddDeviceToOrganization(
        device.serial,
        id_institution
      );
      setUpdateChanges(!updateChanges);
      show(
        'success',
        t('messages.success'),
        t('messages.devices.add_device_to_org_success')
      );
      if (shouldSuggestState(device)) setDeviceToSuggest(device);
    } catch (_error) {
      show(
        'error',
        t('messages.error'),
        t('messages.devices.add_device_to_org_error')
      );
    }
  };

  const handleDeleteDeviceOfOrganization = async (device: DeviceTable) => {
    try {
      await apiServices.devicesApi.devicesControllerRemoveDeviceFromOrganization(
        device.d_id,
        id_institution
      );

      setUpdateChanges(!updateChanges);
      show(
        'success',
        t('messages.success'),
        t('messages.devices.delete_device_success')
      );
    } catch (_error) {
      show(
        'error',
        t('messages.error'),
        t('messages.devices.delete_device_error')
      );
    }
  };

  const showLastDeviceWarning = (device: DeviceTable) => {
    confirmDialog({
      message: (
        <div className="px-5 py-3 text-gray-800 text-base leading-[1.5] text-justify ">
          {t('pages.institutions.form.warningDeleteDeviceMessage')}
        </div>
      ),
      header: (
        <div className="flex items-center gap-2 font-bold text-lg align-items-baseline justify-content-center">
          <i className="pi pi-exclamation-triangle text-xl text-yellow-600" />
          {t('pages.institutions.form.warningDeleteDeviceTitle')}
        </div>
      ),
      acceptLabel: t('yes'),
      acceptClassName: 'p-button-danger',
      style: { width: '36vw', borderRadius: '0.75rem' },
      accept: () => {
        handleDeleteDeviceOfOrganization(device);
      }, // no hacer nada, es solo informativo
    });
  };

  const canEditCreator: boolean = can(
    role,
    AvailableResourceActions.organizations.update_creator,
    admin
  );

  const hasCreator = !!creatorUser || !!institution?.created_by;

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      {isLoading ? (
        <div className="surface-card border-round-2xl col-12 p-3 mt-4">
          <ProgressBarComponent />
        </div>
      ) : (
        <>
          {institution && (
            <>
              <ConfirmDialog />

              <div className="col-12">
                <div className="col-12 shadow-3 md:p-3 surface-card border-round-xl lg:h-full">
                  <div className="p-3 flex align-items-center">
                    <Button
                      icon="pi pi-angle-left"
                      rounded
                      onClick={() => navigate('/authenticated/institutions')}
                      severity="secondary"
                      className="mr-4"
                      aria-label="Bookmark"
                    />
                    {!institution.blocked && (
                      <div className="flex align-items-baseline justify-content-end">
                        <div className="mr-2">
                          <i className="pi pi-building text-2xl text-900"></i>
                        </div>

                        <div className="font-medium text-2xl text-700">
                          {institution.name}
                        </div>
                      </div>
                    )}
                  </div>
                  {!institution.blocked ? (
                    <div className="md:mx-8 pl-4 py-4">
                      <div className="grid formgrid p-fluid">
                        <div className="col-12 md:col-6 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.name')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.name}
                            </span>
                          </label>
                        </div>

                        <div className="col-12 md:col-6 field mb-2">
                          <div className="flex align-items-center gap-2">
                            <label
                              htmlFor="input-institution-o_id"
                              className="font-bold text-gray-800 m-0 white-space-nowrap"
                            >
                              {t('pages.register.o_id')}:
                            </label>
                            <InputText
                              id="input-institution-o_id"
                              name="o_id"
                              value={institution.o_id ?? id_institution ?? ''}
                              readOnly
                              onFocus={(e) => e.target.select()}
                              className="surface-300 text-700 border-none flex-1"
                              style={{ cursor: 'default', boxShadow: 'none' }}
                            />
                            <Button
                              type="button"
                              icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                              severity={copied ? 'success' : 'secondary'}
                              text
                              rounded
                              onClick={handleCopyId}
                              tooltip={copied ? t('my_profile.copied') : t('my_profile.copy')}
                              tooltipOptions={{ position: 'bottom' }}
                              aria-label="Copiar ID"
                              className="flex-none"
                              style={{ width: 'auto' }}
                            />
                          </div>
                        </div>

                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.country')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.country}
                            </span>
                          </label>
                        </div>

                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.city')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.city}
                            </span>
                          </label>
                        </div>

                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.postalCode')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.postal_code}
                            </span>
                          </label>
                        </div>

                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.address')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.street_name}
                            </span>
                          </label>
                        </div>

                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.number')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {institution.street_number}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3">
                      <div className="text-xl font-bold text-700 text-center">
                        {t('pages.institutions.messages.blocked')}
                      </div>
                    </div>
                  )}

                  {/* Línea divisoria para sección de contacto */}
                  <div className="col-12 mb-4">
                    <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                      <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                      <div className="px-2 z-1 surface-50 flex align-items-center">
                        <i className="pi pi-phone text-900 mr-2"></i>
                        <span className="text-900 font-bold">
                          {t('pages.institutions.form.contact')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Datos de contacto*/}
                  <div className="md:mx-8 pl-4 py-4">
                    <div className="grid formgrid p-fluid">
                      <div className="col-12 md:col-6 field mb-2 flex align-items-center gap-3">
                        <div className="flex flex-column gap-1">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.contactname')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {contactData.name}{' '}
                            </span>
                          </label>
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.telephone')}:{' '}
                            <span className="text-lg font-normal text-gray-700">
                              {contactData.phone}{' '}
                            </span>
                          </label>
                          <label className="font-bold text-gray-800 block">
                            {t('pages.institutions.form.email')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {contactData.email}{' '}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Línea divisoria para sección de creador */}
                  <div className="col-12 mb-4">
                    <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                      <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                      <div className="px-2 z-1 surface-50 flex align-items-center">
                        <i className="pi pi-user text-900 mr-2"></i>
                        <span className="text-900 font-bold">
                          {t('pages.institutions.form.creator')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Botón cambiar creador */}
                  {canEditCreator && !editingCreator && (
                    <div className="text-center mb-4">
                      <Button
                        id="button-change-institution-creator"
                        type="button"
                        label={t('pages.institutions.form.changeCreator')}
                        className="p-button-raised"
                        onClick={() => {
                          setSelectedCreatorId(institution.created_by ?? null);
                          setEditingCreator(true);
                        }}
                      />
                    </div>
                  )}

                  {editingCreator ? (
                    <div
                      className="flex flex-column gap-2 mb-4 mx-auto w-full"
                      style={{ maxWidth: '40rem' }}
                    >
                      <Dropdown
                        value={selectedCreatorId}
                        options={availableCreatorUsers?.map((u) => ({
                          label: `${u.name} ${u.surnames} (${u.o_name})`,
                          value: u.u_id,
                        }))}
                        onChange={(e) => setSelectedCreatorId(e.value)}
                        filter
                        className="w-full"
                      />
                      <div className="flex justify-content-between gap-2">
                        <Button
                          id="button-cancel-modify-creator"
                          severity="secondary"
                          icon="pi pi-times"
                          label={t('cancel')}
                          outlined
                          onClick={() => setEditingCreator(false)}
                        />
                        <Button
                          id="button-confirm-modify-creator"
                          severity="secondary"
                          icon="pi pi-save"
                          label={t('save')}
                          className="p-button-raised"
                          disabled={!selectedCreatorId || selectedCreatorId == creatorUser?.u_id}
                          onClick={async () => {
                            if (!selectedCreatorId) return;
                            try {
                              await apiServices.adminApi.adminControllerUpdateOrganizationCreator(
                                { user_id: selectedCreatorId },
                                id_institution
                              );
                              setUpdateChanges(!updateChanges);
                              setEditingCreator(false);
                              show(
                                'success',
                                t('messages.success'),
                                t('messages.org.update_creator_success')
                              );
                            } catch {
                              show(
                                'error',
                                t('messages.error'),
                                t('messages.org.update_creator_error')
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : !hasCreator ? (
                    <div
                      className="flex flex-column align-items-center justify-content-center gap-2 text-center text-600 mb-4 py-5 surface-ground border-round-xl mx-auto w-full"
                      style={{ maxWidth: '40rem' }}
                    >
                      <span className="font-medium">
                        {t('pages.institutions.form.creatorNotFound')}
                      </span>
                    </div>
                  ) : (
                    <div
                      className="surface-card border-1 surface-border border-round-lg mb-4 mx-auto w-full p-3"
                      style={{ maxWidth: '40rem' }}
                    >
                      <div className="flex flex-column gap-1">
                        <label className="font-bold text-gray-800 block m-0">
                          {t('pages.institutions.form.contactname')}:{' '}
                          <span className="text-lg font-normal text-gray-800">
                            {creatorUser
                              ? `${creatorUser.name} ${creatorUser.surnames}`
                              : institution.created_by}
                          </span>
                        </label>
                        {creatorUser?.phone && (
                          <label className="font-bold text-gray-800 block m-0">
                            {t('pages.institutions.form.telephone')}:{' '}
                            <span className="text-lg font-normal text-gray-700">
                              {creatorUser.phone}
                            </span>
                          </label>
                        )}
                        {creatorUser?.email && (
                          <label className="font-bold text-gray-800 block m-0">
                            {t('pages.institutions.form.email')}:{' '}
                            <span className="text-lg font-normal text-gray-800">
                              {creatorUser.email}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Línea divisoria para sección de dispositivos */}
                  <div className="col-12 mb-4">
                    <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                      <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                      <div className="px-2 z-1 surface-50 flex align-items-center">
                        <i className="pi pi-cog text-900 mr-2"></i>
                        <span className="text-900 font-bold">{t('pages.menu.devices')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Botón añadir dispositivo a la organización */}
                  <div className="text-center mb-4">
                    <Button
                      id="button-add-device-to-institution"
                      type="button"
                      label={t('pages.devices.devicePage.institution')}
                      className="p-button-raised"
                      onClick={() => setVisible(true)}
                      visible={canAssignDeviceToInstitution}
                    />
                  </div>

                  {/* Dispositivos asociados a la institución */}
                  {institution.devices?.length === 0 ? (
                    <div
                      className="flex flex-column align-items-center justify-content-center gap-2 text-center text-600 mb-4 py-5 surface-ground border-round-xl mx-auto w-full"
                      style={{ maxWidth: '40rem' }}
                    >
                      <i className="pi pi-cog text-3xl text-400"></i>
                      <span className="font-medium">
                        {t('pages.institutions.form.devicesNotFound')}
                      </span>
                    </div>
                  ) : (
                    <div
                      className="surface-card border-1 surface-border border-round-lg mb-4 mx-auto w-full overflow-hidden"
                      style={{ maxWidth: '40rem' }}
                    >
                      {institution.devices?.map((device, index) => (
                        <div
                          key={device.d_id}
                          className={classNames(
                            'flex align-items-center justify-content-between gap-3 px-3 py-2',
                            { 'border-top-1 surface-border': index > 0 }
                          )}
                        >
                          <div className="flex align-items-center gap-3 min-w-0">
                            <i className="pi pi-cog text-500"></i>
                            <span className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                              {device.serial}
                            </span>
                          </div>
                          <Button
                            icon="pi pi-trash"
                            rounded
                            text
                            severity="danger"
                            onClick={() =>
                              institution.devices.length === 1
                                ? showLastDeviceWarning(device)
                                : handleDeleteDeviceOfOrganization(device)
                            }
                            tooltip={t('deleteDevice')}
                            tooltipOptions={{
                              position: 'bottom',
                              mouseTrack: true,
                              mouseTrackTop: 15,
                            }}
                            aria-label="Delete"
                            visible={canDeleteDeviceFromInstitution}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Eliminar organización */}
                  {canDeleteInstitution && (
                    <>
                      <div className="col-12 mb-4">
                        <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                          <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                          <div className="px-2 z-1 surface-50 flex align-items-center">
                            <i className="pi pi-trash text-900 mr-2"></i>
                            <span className="text-900 font-bold">{t('deleteInstitution')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-content-center my-4">
                        <Button
                          icon="pi pi-times"
                          label={t('deleteInstitution')}
                          onClick={(e) => confirmDeleteInstitution(id_institution, e)}
                          severity="danger"
                          aria-label="Eliminar"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          <ModalAssignDeviceToOrganization
            visible={visible}
            setVisible={setVisible}
            assignDeviceToOrganizationFunction={
              assignDeviceToOrganizationFunction
            }
            o_id={id_institution}
          />
          <ModalSuggestDeviceState
            visible={!!deviceToSuggest}
            onHide={() => setDeviceToSuggest(null)}
            deviceId={deviceToSuggest?.d_id}
            currentState={deviceToSuggest?.device_state as DeviceState}
            allowedStates={DEVICE_CATEGORIES.on_client}
            onSaved={() => setUpdateChanges(!updateChanges)}
          />
        </>
      )}
    </div>
  );
}

export default Institution;